import { useEffect, useRef, useState } from "react";

const MEDIA = [
    {
        type: "video",
        src: "https://customer-assets-4nw71qhi.emergentagent.net/job_cmrenov-estimator/artifacts/73urjriy_VID-20260809-WA0014.mp4",
        title: "Le geste juste",
        caption: "Rythme du chantier · Île-de-France",
    },
    {
        type: "image",
        src: "https://customer-assets-4nw71qhi.emergentagent.net/job_cmrenov-estimator/artifacts/wockkpp9_Screenshot_20260811_173707_WhatsAppBusiness.webp",
        title: "L'ossature",
        caption: "Étaiement & structure porteuse",
    },
    {
        type: "video",
        src: "https://customer-assets-4nw71qhi.emergentagent.net/job_cmrenov-estimator/artifacts/5lwb9jcg_VID-20260803-WA0024.mp4",
        title: "Matière brute",
        caption: "Gros œuvre · maçonnerie fine",
    },
    {
        type: "video",
        src: "https://customer-assets-4nw71qhi.emergentagent.net/job_cmrenov-estimator/artifacts/snqt17ch_VID-20260803-WA0028%281%29.mp4",
        title: "Compagnons",
        caption: "Le savoir-faire, main dans la main",
    },
    {
        type: "video",
        src: "https://customer-assets-4nw71qhi.emergentagent.net/job_cmrenov-estimator/artifacts/5cry2utd_VID-20260803-WA0028%282%29.mp4",
        title: "L'ouvrage",
        caption: "Précision d'exécution",
    },
];

function MediaCard({ item, index, active, onEnter }) {
    const videoRef = useRef(null);
    const cardRef = useRef(null);
    const [loaded, setLoaded] = useState(false);

    // Lazy-load video via IntersectionObserver
    useEffect(() => {
        if (item.type !== "video") return;
        const el = cardRef.current;
        if (!el) return;
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) {
                        setLoaded(true);
                        io.disconnect();
                    }
                });
            },
            { rootMargin: "200px" },
        );
        io.observe(el);
        return () => io.disconnect();
    }, [item.type]);

    // Auto play/pause based on visibility to save resources
    useEffect(() => {
        if (item.type !== "video" || !loaded) return;
        const v = videoRef.current;
        if (!v) return;
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) v.play().catch(() => {});
                    else v.pause();
                });
            },
            { threshold: 0.25 },
        );
        io.observe(v);
        return () => io.disconnect();
    }, [loaded, item.type]);

    return (
        <div
            ref={cardRef}
            data-testid={`atelier-card-${index}`}
            onMouseEnter={() => onEnter(index)}
            className={`relative group flex-shrink-0 w-[70vw] sm:w-[46vw] md:w-[28vw] lg:w-[22vw] aspect-[9/14] overflow-hidden bg-[#0e0f0e] transition-all duration-700 ${
                active === index
                    ? "scale-100 opacity-100"
                    : "scale-[0.97] opacity-70"
            }`}
        >
            {item.type === "video" && loaded && (
                <video
                    ref={videoRef}
                    src={item.src}
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    className="w-full h-full object-cover"
                />
            )}
            {item.type === "video" && !loaded && (
                <div className="w-full h-full flex items-center justify-center text-[#B89C6A]/40 tracking-luxe text-[10px]">
                    ● Chargement
                </div>
            )}
            {item.type === "image" && (
                <img
                    src={item.src}
                    alt={item.title}
                    className="w-full h-full object-cover"
                />
            )}

            {/* Cinematic overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#0e0f0e] via-[#0e0f0e]/10 to-transparent pointer-events-none" />

            {/* Index */}
            <div className="absolute top-4 left-4 text-[#B89C6A] font-serif-lux text-lg">
                0{index + 1}
                <span className="text-[#FAF9F6]/40 text-xs ml-1">
                    / 0{MEDIA.length}
                </span>
            </div>

            {/* Playing indicator */}
            {item.type === "video" && (
                <div className="absolute top-4 right-4 flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-[#FAF9F6]/70">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#B89C6A] animate-pulse" />
                    LIVE
                </div>
            )}

            {/* Caption */}
            <div className="absolute inset-x-0 bottom-0 p-5 md:p-6">
                <div className="tracking-luxe text-[#B89C6A] text-[9px]">
                    {item.caption}
                </div>
                <h3 className="font-serif-lux text-xl md:text-2xl mt-2 text-[#FAF9F6] leading-tight">
                    {item.title}
                </h3>
            </div>

            {/* Hover reveal border */}
            <div
                className={`absolute inset-0 border transition-all duration-500 pointer-events-none ${
                    active === index
                        ? "border-[#B89C6A]"
                        : "border-transparent"
                }`}
            />
        </div>
    );
}

export default function Atelier() {
    const [active, setActive] = useState(0);
    const scrollerRef = useRef(null);

    return (
        <section
            id="atelier"
            data-testid="atelier"
            className="py-32 bg-[#0e0f0e] text-[#FAF9F6] relative overflow-hidden"
        >
            {/* Ambient champagne halo */}
            <div className="pointer-events-none absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-[#B89C6A]/10 blur-[120px]" />

            <div className="max-w-[1500px] mx-auto px-6 md:px-12 relative">
                <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16 section-fade">
                    <div className="max-w-2xl">
                        <div className="flex items-center gap-3 mb-6">
                            <span className="h-px w-10 bg-[#B89C6A]" />
                            <span className="tracking-luxe text-[#B89C6A]">
                                L'Atelier · Coulisses
                            </span>
                        </div>
                        <h2
                            className="font-serif-lux text-4xl md:text-5xl lg:text-6xl leading-[1.02]"
                            style={{ letterSpacing: "-0.015em" }}
                        >
                            Le <em className="italic text-[#B89C6A]">
                                geste
                            </em>
                            , saisi sur le vif.
                        </h2>
                        <p className="mt-6 text-[14px] leading-relaxed text-[#FAF9F6]/60 max-w-md">
                            Cinq instantanés de nos chantiers en Île-de-France. Filmés au fil de l'ouvrage, sans mise en scène — la matière, la main, la mesure.
                        </p>
                    </div>

                    <div className="flex gap-3 items-center">
                        <button
                            data-testid="atelier-prev"
                            onClick={() => {
                                scrollerRef.current?.scrollBy({
                                    left: -400,
                                    behavior: "smooth",
                                });
                            }}
                            className="w-12 h-12 border border-[#FAF9F6]/25 hover:border-[#B89C6A] hover:bg-[#B89C6A]/10 transition-all flex items-center justify-center text-lg"
                            aria-label="Précédent"
                        >
                            ←
                        </button>
                        <button
                            data-testid="atelier-next"
                            onClick={() => {
                                scrollerRef.current?.scrollBy({
                                    left: 400,
                                    behavior: "smooth",
                                });
                            }}
                            className="w-12 h-12 border border-[#FAF9F6]/25 hover:border-[#B89C6A] hover:bg-[#B89C6A]/10 transition-all flex items-center justify-center text-lg"
                            aria-label="Suivant"
                        >
                            →
                        </button>
                    </div>
                </div>

                {/* Horizontal reel */}
                <div
                    ref={scrollerRef}
                    className="flex gap-5 overflow-x-auto overflow-y-hidden snap-x snap-mandatory pb-4 -mx-6 px-6 md:mx-0 md:px-0 section-fade"
                    style={{ scrollbarWidth: "thin" }}
                >
                    {MEDIA.map((m, i) => (
                        <div key={i} className="snap-start">
                            <MediaCard
                                item={m}
                                index={i}
                                active={active}
                                onEnter={setActive}
                            />
                        </div>
                    ))}
                </div>

                {/* Bottom marquee-like meta */}
                <div className="mt-12 pt-8 border-t border-[#FAF9F6]/10 flex flex-wrap items-center justify-between gap-4 section-fade">
                    <div className="flex items-center gap-6">
                        <span className="tracking-luxe text-[#B89C6A]">
                            Silent film
                        </span>
                        <span className="text-[12px] text-[#FAF9F6]/50">
                            {String(active + 1).padStart(2, "0")} /{" "}
                            {String(MEDIA.length).padStart(2, "0")} ·{" "}
                            {MEDIA[active]?.title}
                        </span>
                    </div>
                    <span className="text-[10px] tracking-[0.32em] uppercase text-[#FAF9F6]/40">
                        © CM RENOV · Chantiers 2025
                    </span>
                </div>
            </div>
        </section>
    );
}
